<template>
  <div class="payment-methods-page">
    <AccountPageTopBar :title="$t('paymentMethods.title')" @back="onBack" />

    <main class="payment-methods-page__content">
      <p class="payment-methods-page__desc">{{ $t('paymentMethods.description') }}</p>

      <section v-if="loading" class="payment-methods-state">
        <span>{{ $t('loading') }}</span>
      </section>

      <section v-else-if="loadFailed" class="payment-methods-state">
        <span>{{ $t('paymentMethods.loadFailed') }}</span>
        <button type="button" @click="loadAccounts">{{ $t('retry') }}</button>
      </section>

      <section v-else-if="accounts.length === 0" class="payment-methods-empty">
        <div class="payment-methods-empty__icon" aria-hidden="true">
          <span class="wallet-icon wallet-icon--large"></span>
          <span class="payment-methods-empty__plus">+</span>
        </div>
        <h2>{{ $t('paymentMethods.addTitle') }}</h2>
        <p>{{ $t('paymentMethods.addDesc') }}</p>
        <button type="button" :disabled="authorizing" @click="addAccount">
          {{ authorizing ? $t('loading') : $t('paymentMethods.addAccount') }}
        </button>
      </section>

      <section v-else class="payment-methods-list">
        <article v-for="account in accounts" :key="account.id" class="payment-account-card">
          <div class="payment-account-card__top">
            <span class="payment-account-card__icon" aria-hidden="true">
              <span class="wallet-icon"></span>
            </span>
            <strong>DANA</strong>
            <span class="payment-account-card__status" :class="`payment-account-card__status--${statusClass(account)}`">
              {{ statusText(account) }}
            </span>
          </div>

          <div class="payment-account-card__number">
            <span>{{ displayAccount(account) }}</span>
            <span class="payment-account-card__hidden" aria-hidden="true">
              <van-icon name="closed-eye" />
            </span>
          </div>

          <div class="payment-account-card__bottom">
            <span class="payment-account-card__time">
              <van-icon name="clock-o" />
              {{ authorizationTimeText(account) }}
            </span>
            <button
              v-if="isActive(account)"
              type="button"
              class="payment-account-card__action payment-account-card__action--cancel"
              :disabled="submittingId === account.id"
              @click="cancelAccount(account)"
            >
              {{ $t('paymentMethods.cancelAuthorization') }}
            </button>
            <button
              v-else
              type="button"
              class="payment-account-card__action payment-account-card__action--primary"
              :disabled="authorizing"
              @click="addAccount"
            >
              {{ $t('paymentMethods.reauthorize') }}
            </button>
          </div>
        </article>
      </section>
    </main>
  </div>
</template>

<script>
import { Dialog, Icon, Toast } from 'vant';
import AccountPageTopBar from '@/components/AccountPageTopBar/index.vue';
import { applyDirectDebit, cancelDirectDebit, queryDirectDebitList } from '@/api/payment';
import { clearWithholdingAuthReturnQuery, isWithholdingAuthReturn } from '@/common/withholdingAuth';
import { LANGUAGE } from '@/common/constants';

const ACTIVE_STATUS = 'ACTIVED';
const STATUS_CLASS_MAP = {
  ACTIVED: 'active',
  AUTHORIZING: 'pending',
  FAILED: 'expired',
  INACTIVED: 'disconnected'
};

export default {
  name: 'PaymentMethods',
  components: {
    AccountPageTopBar,
    'van-icon': Icon
  },
  data() {
    return {
      accounts: [],
      loading: false,
      loadFailed: false,
      authorizing: false,
      submittingId: ''
    };
  },
  mounted() {
    this.loadAccounts({
      silent: isWithholdingAuthReturn(this.$route)
    });
  },
  methods: {
    onBack() {
      this.$router.replace(this.resolveBackTarget());
    },
    resolveBackTarget() {
      const fallback = '/profile/index';
      const rawTarget = String(this.$route.query.backTo || '').trim();
      if (!rawTarget) return fallback;
      if (/^(?:https?:)?\/\//i.test(rawTarget) || /^javascript:/i.test(rawTarget)) {
        return fallback;
      }
      return rawTarget.charAt(0) === '/' ? rawTarget : `/${rawTarget}`;
    },
    normalizeAccount(raw = {}) {
      return {
        id: String(raw.id || raw.Id || ''),
        tokenStatus: String(raw.tokenStatus || '').trim().toUpperCase(),
        operationTime: raw.operationTime,
        userLoginId: raw.userLoginId || ''
      };
    },
    async loadAccounts({ silent = false } = {}) {
      this.loading = !silent;
      this.loadFailed = false;
      try {
        const res = await queryDirectDebitList();
        if (res?.code !== 'Success') throw new Error(res?.message || '');
        const list = Array.isArray(res.data) ? res.data : [];
        this.accounts = list.map(this.normalizeAccount).filter(item => item.id);
        if (isWithholdingAuthReturn(this.$route)) {
          this.$store.dispatch('user/fetchMemberInfo', { _showLoading: false }).catch(() => {});
        }
        clearWithholdingAuthReturnQuery(this.$router, this.$route);
      } catch (e) {
        this.loadFailed = true;
        this.accounts = [];
        if (!silent) Toast({ message: e?.message || this.$t('paymentMethods.loadFailed'), duration: 3000 });
      } finally {
        this.loading = false;
      }
    },
    async addAccount() {
      if (this.authorizing) return;
      this.authorizing = true;
      try {
        const res = await applyDirectDebit({ route: this.$route });
        if (res && res.code === 'Success' && res.data && res.data.authUrl) {
          window.location.href = res.data.authUrl;
          return;
        }
        Toast({ message: res?.message || this.$t('systemError'), duration: 3000 });
      } catch (e) {
        Toast({ message: e?.message || this.$t('systemError'), duration: 3000 });
      } finally {
        this.authorizing = false;
      }
    },
    cancelAccount(account) {
      if (!account || !account.id || this.submittingId) return;
      Dialog.confirm({
        title: this.$t('paymentMethods.cancelTitle'),
        message: this.$t('paymentMethods.cancelDesc'),
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel')
      })
        .then(() => this.doCancelAccount(account))
        .catch(() => {});
    },
    async doCancelAccount(account) {
      this.submittingId = account.id;
      try {
        const res = await cancelDirectDebit({ id: account.id });
        if (res?.code !== 'Success') throw new Error(res?.message || '');
        Toast({ message: this.$t('paymentMethods.cancelSuccess'), duration: 2000 });
        await this.loadAccounts({ silent: true });
        this.$store.dispatch('user/fetchMemberInfo', { _showLoading: false }).catch(() => {});
      } catch (e) {
        Toast({ message: e?.message || this.$t('systemError'), duration: 3000 });
      } finally {
        this.submittingId = '';
      }
    },
    isActive(account) {
      return String(account?.tokenStatus || '').toUpperCase() === ACTIVE_STATUS;
    },
    isUnauthorized(account) {
      return String(account?.tokenStatus || '').toUpperCase() === 'INACTIVED';
    },
    authorizationTimeText(account) {
      const key = this.isUnauthorized(account) ? 'unauthorizedAt' : 'authorizedAt';
      return this.$t(`paymentMethods.${key}`, { time: this.formatOperationTime(account?.operationTime) });
    },
    displayAccount(account) {
      const source = String(account?.userLoginId || '').trim();
      return source ? this.maskUserLoginId(source) : this.maskAccount('');
    },
    statusClass(account) {
      return STATUS_CLASS_MAP[String(account?.tokenStatus || '').toUpperCase()] || 'disconnected';
    },
    statusText(account) {
      const key = String(account?.tokenStatus || '').toUpperCase();
      const statusKey = STATUS_CLASS_MAP[key] || 'disconnected';
      return this.$t(`paymentMethods.status.${statusKey}`);
    },
    maskAccount(value) {
      const raw = String(value || '').trim();
      if (!raw) return '••••••••';
      return '••••••••';
    },
    maskUserLoginId(value) {
      const raw = String(value || '').trim();
      if (!raw) return '••••••••';
      const digits = raw.replace(/\D/g, '');
      if (!digits) return '••••••••';
      if (digits.length <= 4) return digits;
      return `${digits.slice(0, 2)}••••${digits.slice(-2)}`;
    },
    formatOperationTime(value) {
      const numeric = Number(value || 0);
      if (!numeric) return '--';
      const timestamp = numeric < 1000000000000 ? numeric * 1000 : numeric;
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) return '--';
      const locale = this.$i18n.locale === LANGUAGE.id ? 'id-ID' : 'en-US';
      const dateText = new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).format(date);
      const timeText = new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(date);
      return `${dateText} · ${timeText}`;
    }
  }
};
</script>

<style lang="less" scoped>
.payment-methods-page {
  min-height: 100vh;
  background: #eef6ff;
  color: #020817;
}

.payment-methods-page__content {
  padding: 96px 24px 48px;
  box-sizing: border-box;
}

.payment-methods-page__desc {
  margin: 0 -24px;
  padding: 30px 24px 24px;
  border-top: 1px solid #dbe4ef;
  color: #5f6b7a;
  font-size: 26px;
  line-height: 36px;
  background: #eef6ff;
}

.payment-methods-state {
  display: flex;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 24px;
  color: #64748b;
  font-size: 28px;

  button {
    min-width: 180px;
    height: 72px;
    padding: 0 28px;
    border: 0;
    border-radius: 12px;
    background: #2563ff;
    color: #ffffff;
    font-size: 26px;
    font-weight: 600;
  }
}

.payment-methods-empty {
  display: flex;
  margin-top: 0;
  padding: 44px 32px 32px;
  align-items: center;
  flex-direction: column;
  border: 1px solid #d9e2ef;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.06);
  text-align: center;

  h2 {
    margin: 28px 0 0;
    color: #020817;
    font-size: 30px;
    font-weight: 700;
    line-height: 42px;
  }

  p {
    margin: 14px 24px 0;
    color: #64748b;
    font-size: 26px;
    line-height: 38px;
  }

  button {
    width: 100%;
    height: 68px;
    margin-top: 34px;
    border: 0;
    border-radius: 12px;
    background: #2563ff;
    box-shadow: 0 12px 24px rgba(37, 99, 255, 0.24);
    color: #ffffff;
    font-size: 26px;
    font-weight: 700;

    &:disabled {
      background: #cbd5e1;
      box-shadow: none;
    }
  }
}

.payment-methods-empty__icon {
  position: relative;
  display: flex;
  width: 112px;
  height: 112px;
  align-items: center;
  justify-content: center;
  border-radius: 24px;
  background: #edf4ff;
}

.payment-methods-empty__plus {
  position: absolute;
  right: -4px;
  bottom: -4px;
  display: flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border: 4px solid #ffffff;
  border-radius: 50%;
  background: #2563ff;
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  line-height: 34px;
}

.payment-methods-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.payment-account-card {
  padding: 30px 28px 24px;
  border: 1px solid #d9e2ef;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.04);
  box-sizing: border-box;
}

.payment-account-card__top {
  display: flex;
  align-items: center;
  gap: 16px;

  strong {
    min-width: 0;
    flex: 1;
    color: #020817;
    font-size: 28px;
    font-weight: 700;
    line-height: 40px;
  }
}

.payment-account-card__icon {
  display: flex;
  width: 56px;
  height: 56px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: #edf4ff;
}

.wallet-icon {
  position: relative;
  display: block;
  width: 30px;
  height: 24px;
  border: 4px solid #2563ff;
  border-radius: 6px;
  box-sizing: border-box;

  &::before {
    position: absolute;
    top: -10px;
    left: 3px;
    width: 22px;
    height: 12px;
    border: 4px solid #2563ff;
    border-bottom: 0;
    border-radius: 8px 8px 0 0;
    content: '';
    box-sizing: border-box;
  }

  &::after {
    position: absolute;
    right: -8px;
    top: 6px;
    width: 12px;
    height: 10px;
    border: 4px solid #2563ff;
    border-radius: 5px;
    background: #edf4ff;
    content: '';
    box-sizing: border-box;
  }

  &--large {
    width: 44px;
    height: 34px;
  }
}

.payment-account-card__status {
  display: inline-flex;
  max-width: 180px;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  border-radius: 19px;
  font-size: 22px;
  font-weight: 700;
  line-height: 30px;
  white-space: nowrap;

  &--active {
    background: #e8f8ef;
    color: #008a45;
  }

  &--pending {
    background: #eaf3ff;
    color: #2563ff;
  }

  &--expired {
    background: #fff0df;
    color: #d97706;
  }

  &--disconnected {
    background: #eef2f7;
    color: #64748b;
  }
}

.payment-account-card__number {
  display: flex;
  min-height: 80px;
  margin-top: 22px;
  align-items: center;
  justify-content: center;
  gap: 18px;
  color: #020817;
  font-size: 34px;
  font-weight: 800;
  line-height: 44px;
  letter-spacing: 0;
}

.payment-account-card__hidden {
  display: flex;
  width: 56px;
  height: 56px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #f1f5fb;
  color: #64748b;
  font-size: 30px;
  cursor: pointer;
}

.payment-account-card__bottom {
  display: flex;
  min-height: 58px;
  margin-top: 18px;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.payment-account-card__time {
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: 8px;
  color: #7a8699;
  font-size: 21px;
  line-height: 30px;
}

.payment-account-card__action {
  min-width: 210px;
  height: 56px;
  padding: 0 20px;
  border-radius: 12px;
  font-size: 24px;
  font-weight: 700;
  white-space: nowrap;

  &:disabled {
    opacity: 0.6;
  }

  &--cancel {
    border: 2px solid #2563ff;
    background: #ffffff;
    color: #2563ff;
  }

  &--primary {
    border: 0;
    background: #2563ff;
    box-shadow: 0 10px 20px rgba(37, 99, 255, 0.22);
    color: #ffffff;
  }
}

@media (max-width: 360px) {
  .payment-account-card__bottom {
    align-items: stretch;
    flex-direction: column;
  }

  .payment-account-card__action {
    width: 100%;
  }
}
</style>
